// The onsite queue.
//
// The default view is work in progress, oldest first, per Section 5:
// requested, scheduled and completed jobs stay visible so nothing is
// forgotten. Canceled onsites are history behind a filter. The server scopes
// the list to the viewer: franchisee users see only their own jobs, so this
// page renders whatever comes back without caring who is looking.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  listOnsites,
  removeOnsite,
  importOnsitesFile,
  listFormLinks,
  createFormLink,
  revokeFormLink,
  listFranchisees,
} from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  EmptyState,
  Notice,
  Panel,
  Pill,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { onsiteStatusTone } from '../tones/onsites.js'
import { useApiForm } from '../useApiForm.js'
import { useConfirm } from '../confirm.jsx'
import ImportCard from '../ImportCard.jsx'
import { onsiteStatusLabel, formatCount, formatInstant, SEPARATOR } from '../vocab.js'

function ageDays(createdAt) {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000)
  if (days < 1) return 'Today'
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

// Submission links: the bookmarkable URLs offices use to file an onsite we
// never got a call about. One per franchisee, copyable, revoked rather than
// deleted so past submissions keep their provenance.
function LinksCard({ onClose }) {
  const confirm = useConfirm()
  const [links, setLinks] = useState(null)
  const [franchisees, setFranchisees] = useState([])
  const [franchiseeId, setFranchiseeId] = useState('')
  const [copiedId, setCopiedId] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [rosterError, setRosterError] = useState(null)
  const [revokeError, setRevokeError] = useState(null)
  const { saving, error, submit } = useApiForm()

  const load = useCallback(async () => {
    try {
      const data = await listFormLinks()
      setLinks(data.links)
      setLoadError(null)
    } catch (caught) {
      setLinks([])
      setLoadError(caught.message)
    }
  }, [])

  useEffect(() => {
    load()
    listFranchisees()
      .then((data) => {
        setFranchisees(data.franchisees)
        setRosterError(null)
      })
      .catch((caught) => setRosterError(caught.message))
  }, [load])

  async function create() {
    const result = await submit(() => createFormLink({ kind: 'ONSITE_REPORT', franchiseeId }))
    if (result) {
      setFranchiseeId('')
      load()
    }
  }

  async function copy(link) {
    try {
      await navigator.clipboard.writeText(link.url)
      setCopiedId(link.id)
      setTimeout(() => setCopiedId(null), 1600)
    } catch {
      // The clipboard can be refused (permissions, an insecure context), so
      // the prompt lets them copy by hand.
      window.prompt('Copy the link:', link.url)
    }
  }

  async function revoke(link) {
    if (
      !(await confirm(
        `Revoke the link for ${link.franchiseeName ?? link.label}? Anyone holding it can no longer submit.`,
        { title: 'Revoke the link', confirmLabel: 'Revoke' },
      ))
    ) {
      return
    }
    setRevokeError(null)
    try {
      await revokeFormLink(link.id)
      load()
    } catch (caught) {
      setRevokeError(caught.message)
    }
  }

  return (
    <Panel
      title="Submission links"
      support="A bookmarkable form for offices to file an onsite we never got a call about, no sign-in needed. Each link is bound to one franchisee, so submissions attribute themselves; revoking it shuts the door without touching what already came in."
    >
      {error ? <Notice tone="bad">{error}</Notice> : null}
      {loadError ? <Notice tone="bad">{loadError}</Notice> : null}
      {rosterError ? <Notice tone="bad">{rosterError}</Notice> : null}
      {revokeError ? <Notice tone="bad">{revokeError}</Notice> : null}

      {links === null ? (
        <div className="center-note">Loading</div>
      ) : links.length ? (
        <div className="rows">
          {links.map((link) => (
            <div key={link.id} className={`row${link.isActive ? '' : ' is-dimmed'}`}>
              <div className="row-main">
                <div className="row-primary">{link.franchiseeName ?? link.label}</div>
                <div className="row-meta wraps">
                  {[
                    link.url,
                    `${link.submissionCount} ${link.submissionCount === 1 ? 'submission' : 'submissions'}`,
                  ].join(SEPARATOR)}
                </div>
              </div>
              <div className="row-trail">
                {link.isActive ? (
                  <>
                    <Button onClick={() => copy(link)}>
                      {copiedId === link.id ? 'Copied' : 'Copy link'}
                    </Button>
                    <Button variant="ghost" onClick={() => revoke(link)}>
                      Revoke
                    </Button>
                  </>
                ) : (
                  <Pill tone="neutral">Revoked</Pill>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="No links yet">
          Create one per franchisee and send it to their office.
        </EmptyState>
      )}

      <div className="btnrow">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <span className="spacer" />
        <Select
          value={franchiseeId}
          onChange={setFranchiseeId}
          ariaLabel="Franchisee for the new link"
          style={{ width: '16rem' }}
          options={[
            { value: '', label: 'Choose a franchisee' },
            ...franchisees.map((franchisee) => ({
              value: franchisee.id,
              label: franchisee.name,
            })),
          ]}
        />
        <Button variant="primary" onClick={create} disabled={saving || !franchiseeId}>
          {saving ? 'Creating' : 'New link'}
        </Button>
      </div>
    </Panel>
  )
}

export default function Onsites() {
  const { can } = useAuth()
  const canWrite = can(ACTION.ONSITES_WRITE)
  const canDelete = can(ACTION.RECORDS_DELETE)
  const confirm = useConfirm()
  const [filter, setFilter] = useState('')
  const [onsites, setOnsites] = useState(null)
  const [importing, setImporting] = useState(false)
  const [managingLinks, setManagingLinks] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [rowError, setRowError] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await listOnsites(filter ? { status: filter } : {})
      setOnsites(data.onsites)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [filter])

  useEffect(() => {
    load()
  }, [load])

  async function drop(onsite) {
    if (
      !(await confirm(
        `Remove the ${onsite.customerLabel} onsite entirely? Cancel is the usual way to close a request; removing erases it from every screen.`,
        { title: 'Remove the onsite', confirmLabel: 'Remove onsite' },
      ))
    ) {
      return
    }
    setRowError(null)
    try {
      await removeOnsite(onsite.id)
      load()
    } catch (caught) {
      setRowError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }

  return (
    <>
      {rowError ? <Notice tone="bad">{rowError}</Notice> : null}

      <div className="toolbar">
        <Select
          value={filter}
          onChange={setFilter}
          ariaLabel="Filter by status"
          style={{ width: '14rem' }}
          options={[
            { value: '', label: 'Work in progress' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'CANCELED', label: 'Canceled' },
          ]}
        />
        <span className="spacer" />
        {canWrite ? (
          <>
            <Button variant="ghost" onClick={() => setManagingLinks((open) => !open)}>
              Submission links
            </Button>
            <Button variant="ghost" onClick={() => setImporting((open) => !open)}>
              Import
            </Button>
            <ButtonLink variant="primary" to="/onsites/new">
              New onsite
            </ButtonLink>
          </>
        ) : null}
      </div>

      {managingLinks && canWrite ? <LinksCard onClose={() => setManagingLinks(false)} /> : null}

      {importing && canWrite ? (
        <ImportCard
          id="onsites-file"
          title="Import onsite requests"
          description="CSV or Excel. Columns: customer, and optionally franchisee, requested_for, window_end, site_name, site_address, contact_name, contact_phone, estimated_donors and instructions. A file with any bad row is refused whole. Requested tests and actuals are recorded on each onsite afterwards."
          request={importOnsitesFile}
          describe={(result) => `${result.onsites.length} onsites imported.`}
          onImported={load}
          onClose={() => setImporting(false)}
        />
      ) : null}

      <Panel
        title={filter ? onsiteStatusLabel(filter) : 'Work in progress'}
        support={onsites ? `${onsites.length} shown, oldest first` : 'Loading'}
        flush
      >
        {onsites && onsites.length === 0 ? (
          <EmptyState title={filter ? 'Nothing here' : 'The queue is clear'}>
            {filter ? 'No onsites with that status yet.' : 'New requests land here.'}
          </EmptyState>
        ) : null}

        {onsites && onsites.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Site</Th>
                <Th>Requested for</Th>
                <Th>Assigned to</Th>
                <Th numeric>Est. donors</Th>
                <Th>On-site summary</Th>
                <Th>Status</Th>
                <Th>Age</Th>
                {canDelete ? <Th aria-label="Actions" /> : null}
              </Tr>
            </Thead>
            <Tbody>
              {onsites.map((onsite) => (
                <Tr key={onsite.id}>
                  <Td>
                    <Link to={`/onsites/${onsite.id}`}>{onsite.customerLabel}</Link>
                    {onsite.requestingContactName ? (
                      <div className="ui-cell-meta">
                        {[onsite.requestingContactName, onsite.requestingContactPhone]
                          .filter(Boolean)
                          .join(SEPARATOR)}
                      </div>
                    ) : null}
                  </Td>
                  <Td>{onsite.siteName || <span className="muted">Not set</span>}</Td>
                  <Td>
                    {formatInstant(onsite.requestedAt, { year: false }) ?? (
                      <span className="muted">Not set</span>
                    )}
                  </Td>
                  <Td>{onsite.franchiseeName ?? <span className="muted">Unassigned</span>}</Td>
                  <Td numeric>{formatCount(onsite.estimatedDonorCount) ?? ''}</Td>
                  <Td>
                    {onsite.missingActuals.length ? (
                      <span className="muted">{onsite.missingActuals.length} items</span>
                    ) : (
                      'Summary complete'
                    )}
                  </Td>
                  <Td>
                    <Pill tone={onsiteStatusTone[onsite.status]}>
                      {onsiteStatusLabel(onsite.status)}
                    </Pill>
                  </Td>
                  <Td>{ageDays(onsite.createdAt)}</Td>
                  {canDelete ? (
                    <Td numeric>
                      <Button variant="ghost" onClick={() => drop(onsite)}>
                        Remove
                      </Button>
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Panel>
    </>
  )
}
